import { useState } from 'react';
import { useConfig, useUpdateConfig } from '@/hooks/useConfig';

export default function Dashboard() {
    const { config, rawConfig } = useConfig();
    const updateConfig = useUpdateConfig();

    const [maintenanceMode, setMaintenanceMode] = useState(
        config.isMaintenanceMode ?? false
    );

    const handleMaintenanceToggle = async () => {
        const newValue = !maintenanceMode;
        setMaintenanceMode(newValue);
        await updateConfig.mutateAsync([
            { key: 'isMaintenanceMode', value: newValue },
        ]);
    };

    return (
        <div>
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white font-display mb-2">
                    Dashboard
                </h1>
                <p className="text-zinc-400">
                    Monitor and control your site from the command center.
                </p>
            </div>

            {/* Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <StatusCard
                    title="Site Status"
                    value={maintenanceMode ? 'MAINTENANCE' : 'LIVE'}
                    valueColor={maintenanceMode ? 'text-yellow-500' : 'text-neon-green'}
                    footer="Toggle below to change"
                />
                <StatusCard
                    title="Theme"
                    value={config.archetype?.toUpperCase() || 'DEGEN'}
                    valueColor="text-neon-purple"
                    footer="Current archetype"
                />
                <StatusCard
                    title="Ticker Symbols"
                    value={String(config.tickerSymbols?.length || 0)}
                    valueColor="text-white"
                    footer="Active coins"
                />
            </div>

            {/* Quick Actions */}
            <div className="card mb-8">
                <h2 className="text-xl font-bold text-white mb-6">Quick Actions</h2>

                <div className="space-y-6">
                    {/* Maintenance Toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-white">Maintenance Mode</p>
                            <p className="text-sm text-zinc-500">
                                Disable public access temporarily
                            </p>
                        </div>
                        <button
                            onClick={handleMaintenanceToggle}
                            disabled={updateConfig.isPending}
                            className={`
                relative w-16 h-8 rounded-full transition-colors
                ${maintenanceMode ? 'bg-yellow-500' : 'bg-zinc-700'}
                ${updateConfig.isPending ? 'opacity-50' : ''}
              `}
                        >
                            <span
                                className={`
                  absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform
                  ${maintenanceMode ? 'left-9' : 'left-1'}
                `}
                            />
                        </button>
                    </div>

                    {/* View Site */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-white">View Public Site</p>
                            <p className="text-sm text-zinc-500">
                                Open your site in a new tab
                            </p>
                        </div>
                        <a
                            href="/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost"
                        >
                            Open →
                        </a>
                    </div>
                </div>
            </div>

            {/* Current Config Preview */}
            <div className="card">
                <h2 className="text-xl font-bold text-white mb-4">Configuration</h2>
                <pre className="bg-void rounded-lg p-4 overflow-auto text-sm text-zinc-400 font-mono max-h-64">
                    {JSON.stringify(rawConfig, null, 2)}
                </pre>
            </div>
        </div>
    );
}

function StatusCard({
    title,
    value,
    valueColor = 'text-white',
    footer,
}: {
    title: string;
    value: string;
    valueColor?: string;
    footer: string;
}) {
    return (
        <div className="card">
            <p className="text-sm text-zinc-500 mb-2">{title}</p>
            <p className={`text-3xl font-bold font-display ${valueColor}`}>
                {value}
            </p>
            <p className="text-xs text-zinc-600 mt-2">{footer}</p>
        </div>
    );
}
